import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import { IconDots, IconUsers } from "@tabler/icons-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../../../../components/ui/Button";
import { ShareButton } from "../../../../../components/ui/ShareButton";
import Text from "../../../../../components/ui/Text";
import {
  GAME_PAYMENT_MODE,
  GAME_STATUS,
  INVITE_STATUS,
  PLAYER_ROLES,
} from "../../../../../constants/game";
import { useAuth } from "../../../../../contexts/AuthContext";
import { useToast } from "../../../../../contexts/ToastContext";
import apiService from "../../../../../services/api";
import { formatGameDateTime } from "../../../../../utils/formatters";
import { AddPlayerDialog } from "../AddPlayerDialog";
import { HowAddPlayerDialog } from "../HowAddPlayerDialog";
import { PlayerMenu } from "./parts";

export const PlayerList = ({
  gameDetails,
  players,
  waitlist = [],
  teamSummary,
  maxPlayers,
  fetchPlayers,
  fetchGameDetails,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [playerMenuAnchor, setPlayerMenuAnchor] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  const [isRemoveTeamsDialogOpen, setIsRemoveTeamsDialogOpen] = useState(false);
  const [removingTeams, setRemovingTeams] = useState(false);

  const [isAddPlayerModalOpen, setIsAddPlayerModalOpen] = useState(false);
  const [addingPlayer, setAddingPlayer] = useState(false);

  const [isHowAddPlayerDialogOpen, setIsHowAddPlayerDialogOpen] =
    useState(false);

  const isCurrentUserHost = currentUser?.id === gameDetails.created_by;
  const isCurrentUserPlayer = players.some(
    (player) => player.player_id === currentUser?.id,
  );
  const isCurrentUserInvited = players.some(
    (player) =>
      player.player_id === currentUser?.id &&
      player.invite_status === INVITE_STATUS.PENDING &&
      player.is_invited,
  );

  const showPlayerMenuTrigger = (playerRole, addedBy) =>
    (isCurrentUserHost || addedBy === currentUser?.id) &&
    gameDetails.status === GAME_STATUS.PUBLISHED &&
    playerRole !== PLAYER_ROLES.HOST;

  const handlePlayerMenuOpen = (event, playerId) => {
    setPlayerMenuAnchor(event.currentTarget);
    setSelectedPlayerId(playerId);
  };
  const handlePlayerMenuClose = () => {
    setPlayerMenuAnchor(null);
    setSelectedPlayerId(null);
  };

  const handleRemoveTeams = async () => {
    setRemovingTeams(true);
    try {
      const response = await apiService.deleteTeams(gameDetails.id);
      if (response.success) {
        showToast(response.message, "success");
        fetchPlayers();
        setIsRemoveTeamsDialogOpen(false);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setRemovingTeams(false);
    }
  };

  const handleSubmitAddPlayer = async (newPlayerData) => {
    if (isCurrentUserPlayer && !isCurrentUserHost) {
      //Redirect to booking page
      navigate("/review-booking", {
        state: {
          gameData: gameDetails,
          unregisteredPlayers: [newPlayerData],
        },
      });
      return;
    }
    setAddingPlayer(true);
    const payload = {
      unregistered_players: [
        {
          first_name: newPlayerData.first_name,
          last_name: newPlayerData.last_name,
        },
      ],
    };
    try {
      const response = await apiService.joinGame(gameDetails.id, payload);
      if (response.success) {
        showToast(response.message, "success");

        navigate("/game-success", {
          state: {
            gameDetails,
          },
        });
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsAddPlayerModalOpen(false);
      setAddingPlayer(false);
    }
  };

  // Get game type and player count
  const getGameType = () => {
    if (maxPlayers) {
      const half = maxPlayers / 2;
      return half ? `${half}v${half}` : "";
    }
    return ""; // Default fallback
  };
  // Create share message
  const createShareMessage = () => {
    const dateTime = formatGameDateTime(gameDetails?.datetime);
    const gameType = getGameType();

    // Build player list
    const playersListText = players
      ?.map((player, index) => {
        return `${index + 1}. ${player?.first_name || ""} ${player?.last_name || ""}
`;
      })
      .join("");

    const playerCount = players?.length || 0;

    // Build waiting list, if there is one
    const waitlistText =
      waitlist.length > 0
        ? `
Waiting list (${waitlist.length}):
${waitlist
  .map((entry, index) => {
    return `${index + 1}. ${entry?.first_name || ""} ${entry?.last_name || ""}
`;
  })
  .join("")}`
        : "";

    return `${gameType} ${gameDetails.sport} at ${gameDetails.venue}
${dateTime}

Player List (${playerCount}/${maxPlayers}):
${playersListText}${waitlistText}
Join the game: ${`${window.location.origin}/game-details/${gameDetails.id}`}`;
  };

  // Sort players by team
  const sortedPlayersByTeam = () => {
    // Check if any player has team_id (fallback to old logic)
    const hasTeams = players.some((p) => p.team_id);

    // If no teams exist, return players as a single group
    if (!hasTeams) {
      return [{ teamId: null, teamName: null, players: players }];
    }

    // If team_summary exists, use it to build teams
    if (teamSummary && Object.keys(teamSummary).length > 0) {
      const teamMap = new Map();

      // Initialize teams from team_summary
      Object.entries(teamSummary).forEach(([teamId, teamInfo]) => {
        teamMap.set(teamId, {
          teamId: teamId,
          teamName: teamInfo.team_name,
          players: [],
        });
      });

      // Assign players to their teams
      players.forEach((player) => {
        if (player.team_id && teamMap.has(player.team_id)) {
          teamMap.get(player.team_id).players.push(player);
        }
      });

      const teams = Array.from(teamMap.values());

      // Add players without team (if any)
      const playersWithoutTeam = players.filter((p) => !p.team_id);
      if (playersWithoutTeam.length > 0) {
        teams.push({
          teamId: null,
          teamName: "Unassigned",
          players: playersWithoutTeam,
        });
      }

      return teams;
    }

    // Build team summary from players (fallback)
    const teamMap = new Map();

    players.forEach((player) => {
      if (player.team_id) {
        if (!teamMap.has(player.team_id)) {
          teamMap.set(player.team_id, {
            teamId: player.team_id,
            teamName: player.team_name || `Team ${player.team_id}`,
            players: [],
          });
        }
        teamMap.get(player.team_id).players.push(player);
      }
    });

    const teams = Array.from(teamMap.values());

    // Add players without team (if any)
    const playersWithoutTeam = players.filter((p) => !p.team_id);
    if (playersWithoutTeam.length > 0) {
      teams.push({
        teamId: null,
        teamName: "Unassigned",
        players: playersWithoutTeam,
      });
    }

    return teams;
  };

  const playerGroups = sortedPlayersByTeam();

  return (
    <Box sx={{ p: { xs: 0, md: 3 }, pt: { xs: 0, md: 0 } }}>
      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          borderRadius: 2,
          p: 2,
        }}
      >
        {/* Player List Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Text variant="paragraph" sx={{ fontSize: "16px", fontWeight: 500 }}>
            Player list ({players.length} / {maxPlayers})
          </Text>
          <ShareButton text={createShareMessage()} />
        </Box>
        {/* Player List - Grouped by Teams */}
        {playerGroups.map((group, groupIndex) => (
          <Box
            key={group.teamId || "no-team"}
            sx={{
              mb: groupIndex < playerGroups.length - 1 ? 3 : 0,
            }}
          >
            {/* Team Name Header - Only show if teams exist */}
            {group.teamName && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Text variant="paragraph" component="p" sx={{ mr: "auto" }}>
                  {group.teamName}
                </Text>
                <IconUsers size={16} style={{ marginTop: "-2px" }} />
                <Text variant="paragraph" component="p" sx={{ ml: 0.5 }}>
                  {group.players.length}
                </Text>
              </Box>
            )}

            <Stack spacing={2}>
              {/* Dynamic Player List */}
              {group.players.map((player) => (
                <Box
                  key={player.player_id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: "white",
                    p: 1,
                    borderRadius: "8px",
                  }}
                >
                  <Avatar
                    src={player?.profile_image_url}
                    sx={{ width: 40, height: 40, fontSize: "16px" }}
                  >
                    {player?.first_name?.[0].toUpperCase()}
                    {player?.last_name?.[0].toUpperCase()}
                  </Avatar>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Text
                        variant="paragraph"
                        sx={{ fontSize: "16px", fontWeight: 500 }}
                      >
                        {player.first_name} {player.last_name}
                      </Text>
                      {player.added_by_name && (
                        <Text
                          variant="paragraph"
                          sx={{
                            fontSize: "12px",
                            lineHeight: "14px",
                            color: "#737373",
                          }}
                        >
                          Added by {player.added_by_name}
                        </Text>
                      )}
                    </Box>

                    {/* Show payment status chip for non-host players, only in self-payment mode. Just for host */}
                    {player.role !== PLAYER_ROLES.HOST &&
                      gameDetails.payment_mode === GAME_PAYMENT_MODE.SELF &&
                      isCurrentUserHost &&
                      ((player.invite_status !== INVITE_STATUS.PENDING &&
                        player.is_invited) ||
                        !player.is_invited) && (
                        <Chip
                          label={player.paid ? "Paid" : "Unpaid"}
                          size="small"
                          sx={{
                            fontSize: "12px",
                            color: player.paid ? "#008236" : "#525252",
                            mt: 0.5,
                            backgroundColor: player.paid
                              ? "#DCFCE7"
                              : "#ebebeb",
                          }}
                        />
                      )}

                    {/* Show invite status chip for non-host players. Just for host */}
                    {player.is_invited &&
                      (isCurrentUserHost || isCurrentUserInvited) && (
                        <Chip
                          label={
                            player.invite_status === INVITE_STATUS.PENDING
                              ? "Invited"
                              : player.invite_status === INVITE_STATUS.ACCEPTED
                                ? "Accepted"
                                : "Declined"
                          }
                          size="small"
                          sx={{
                            fontSize: "12px",
                            color:
                              player.invite_status === INVITE_STATUS.PENDING
                                ? "#FF6900"
                                : player.invite_status ===
                                    INVITE_STATUS.ACCEPTED
                                  ? "#008236"
                                  : "#FF4D4F",
                            mt: 0.5,
                            backgroundColor:
                              player.invite_status === INVITE_STATUS.PENDING
                                ? "#FFF7ED"
                                : player.invite_status ===
                                    INVITE_STATUS.ACCEPTED
                                  ? "#DCFCE7"
                                  : "#FFF1F0",
                          }}
                        />
                      )}
                  </Box>
                  {player.role === PLAYER_ROLES.HOST && (
                    <Chip
                      label="Host"
                      size="small"
                      sx={{
                        backgroundColor: "#E6F4FF",
                        color: "#1890FF",
                        fontSize: "12px",
                        px: 1,
                        height: "24px",
                      }}
                    />
                  )}
                  {/* Show 3-dot menu only if current user is host and not viewing from history and not cancelled */}
                  {showPlayerMenuTrigger(player.role, player.added_by) && (
                    <IconButton
                      size="small"
                      onClick={(event) =>
                        handlePlayerMenuOpen(event, player.player_id)
                      }
                      sx={{
                        color: "#A1A1A1",
                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },
                      }}
                    >
                      <IconDots size={16} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
        {(isCurrentUserHost || isCurrentUserPlayer) &&
          gameDetails.status === GAME_STATUS.PUBLISHED && (
            <Button
              variant="tertiary"
              onClick={() => {
                isCurrentUserHost
                  ? setIsHowAddPlayerDialogOpen(true)
                  : setIsAddPlayerModalOpen(true);
              }}
              sx={{
                width: "auto",
                mt: 2,
                fontSize: "16px",
                py: 1.5,
              }}
            >
              + Add Player
            </Button>
          )}
      </Box>

      {/* Waiting List */}
      {waitlist.length > 0 && (
        <Box
          sx={{
            backgroundColor: "#F5F5F5",
            borderRadius: 2,
            p: 2,
            mt: 4,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Text
              variant="paragraph"
              sx={{ fontSize: "16px", fontWeight: 500 }}
            >
              Waiting list ({waitlist.length})
            </Text>
          </Box>
          <Stack spacing={2}>
            {waitlist.map((entry) => (
              <Box
                key={entry.user_id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "white",
                  p: 1,
                  borderRadius: "8px",
                }}
              >
                <Avatar sx={{ width: 40, height: 40, fontSize: "16px" }}>
                  {entry?.first_name?.[0]?.toUpperCase()}
                  {entry?.last_name?.[0]?.toUpperCase()}
                </Avatar>
                <Text
                  variant="paragraph"
                  sx={{ fontSize: "16px", fontWeight: 500, flex: 1 }}
                >
                  {entry.first_name} {entry.last_name}
                </Text>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {isCurrentUserHost &&
        gameDetails.status === GAME_STATUS.PUBLISHED &&
        playerGroups.every((group) => !group.teamId) &&
        players.length > 1 && (
          <Button
            component={Link}
            to={`/generating-teams/${gameDetails.id}`}
            variant="secondary"
            sx={{
              width: "auto",
              mt: 2,
              fontSize: "16px",
              py: 1.5,
            }}
          >
            Generate teams
          </Button>
        )}
      {isCurrentUserHost &&
        gameDetails.status === GAME_STATUS.PUBLISHED &&
        playerGroups.some((group) => group.teamId) && (
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              onClick={() => {
                // Build team_summary from playerGroups
                const teamSummary = {};
                playerGroups.forEach((group) => {
                  if (group.teamId) {
                    teamSummary[group.teamId] = {
                      team_name: group.teamName,
                    };
                  }
                });

                navigate(`/manage-teams/${gameDetails.id}`, {
                  state: {
                    count: players.length,
                    data: players,
                    max_players: maxPlayers,
                    spots_remaining: maxPlayers - players.length,
                    team_summary: teamSummary,
                    gameId: gameDetails.id,
                  },
                });
              }}
              variant="secondary"
              sx={{
                width: "auto",
              }}
            >
              Manage teams
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setIsRemoveTeamsDialogOpen(true);
              }}
              sx={{
                width: "auto",
                color: "#FF4D4F",
                borderColor: "#FF4D4F",
                "&:hover": {
                  backgroundColor: "#FFF2F0",
                  borderColor: "#FF4D4F",
                },
              }}
            >
              Remove teams
            </Button>
          </Box>
        )}

      {/* Player Menu */}
      <PlayerMenu
        isCurrentUserHost={isCurrentUserHost}
        isAddedByCurrentUser={
          players.find((p) => p.player_id === selectedPlayerId)?.added_by ===
          currentUser?.id
        }
        isPlayerPaid={
          players.find((p) => p.player_id === selectedPlayerId)?.paid
        }
        isPlayerInvited={
          players.find((p) => p.player_id === selectedPlayerId)
            ?.invite_status === INVITE_STATUS.PENDING
        }
        playerMenuAnchor={playerMenuAnchor}
        handlePlayerMenuClose={handlePlayerMenuClose}
        selectedPlayerId={selectedPlayerId}
        gameId={gameDetails.id}
        gamePaymentMode={gameDetails.payment_mode}
        players={players}
        fetchPlayers={fetchPlayers}
        fetchGameDetails={fetchGameDetails}
      />

      {/* How to add player dialog */}
      <HowAddPlayerDialog
        isOpen={isHowAddPlayerDialogOpen}
        onClose={() => setIsHowAddPlayerDialogOpen(false)}
        handleInviteExistingMember={() => {
          setIsHowAddPlayerDialogOpen(false);
          navigate(`/invite-members`, {
            state: {
              gameId: gameDetails.id,
              groupId: gameDetails.group_id,
              playersInGame: players.map((p) => p.player_id),
            },
          });
        }}
        handleAddManually={() => {
          setIsHowAddPlayerDialogOpen(false);
          setIsAddPlayerModalOpen(true);
        }}
      />

      {/* Add Player Modal */}
      <AddPlayerDialog
        isOpen={isAddPlayerModalOpen}
        onClose={() => {
          setIsAddPlayerModalOpen(false);
        }}
        onSubmit={handleSubmitAddPlayer}
        players={players}
        loading={addingPlayer}
      />

      {/* Remove Teams Confirmation Dialog */}
      <Dialog
        open={isRemoveTeamsDialogOpen}
        onClose={() => {
          setIsRemoveTeamsDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "12px", m: 2, width: "310px" } },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ mb: 0, fontSize: "20px" }}>
            Remove teams
          </Text>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph" sx={{ fontSize: "16px" }}>
            Are you sure you want to remove teams?
          </Text>
        </DialogContent>

        <Box
          sx={{
            p: 3,
            pt: 1,
            gap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Button
            variant="primary"
            onClick={handleRemoveTeams}
            disabled={removingTeams}
            sx={{
              flex: 1,
              fontSize: "16px",
            }}
          >
            {removingTeams ? "Removing..." : "Yes, remove teams"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setIsRemoveTeamsDialogOpen(false);
            }}
            sx={{ flex: 1, fontSize: "16px" }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};
